// Importation des fonctions nécessaires de vue-router
import { createRouter, createWebHistory } from 'vue-router';

// Importation des composants
import Form from '../components/Form.vue';
import AgentDetail from '../components/details/AgentDetail.vue';
import DigitalEntityDetail from '../components/details/DigitalEntityDetail.vue';
import LocationDetail from '../components/details/LocationDetail.vue';
import MaterialEntityDetail from '../components/details/MaterialEntityDetail.vue';
import MemosDetail from '../components/details/MemosDetail.vue';
import ProjectDetail from '../components/details/ProjectDetail.vue';
import Home from '../views/Home.vue';
import NotFound from '../views/NotFound.vue';
import Actualites from '../views/general/Actualites.vue';
import CasUsage from '../views/general/CasUsage.vue';
import Contact from '../views/general/Contact.vue';
import Fonctionnalites from '../views/general/Fonctionnalites.vue';
import Legal from '../views/general/Legal.vue';

// Importation des vues d'authentification
import AuthPage from '../views/auth/AuthPage.vue';
import EmailValidation from '../views/auth/EmailValidation.vue';

// Importation des vues utilisateur
import CollectionView from '../views/user/CollectionView.vue';
import CollectionsManagement from '../views/user/CollectionsManagement.vue';
import GestionMemos from '../views/user/GestionMemos.vue';
import MyAgents from '../views/user/MyAgents.vue';
import MyDigitalEntities from '../views/user/MyDigitalEntities.vue';
import MyLocations from '../views/user/MyLocations.vue';
import MyMaterialEntities from '../views/user/MyMaterialEntities.vue';

import MyProfileEditor from '../views/user/MyProfileEditor.vue';
import MyProjects from '../views/user/MyProjects.vue';
import UserDashboard from '../views/user/UserDashboard.vue';
import InteractiveMap from '../views/user/tools/InteractiveMap.vue';

// Importation des vues administratives
import AdminDashboard from '../views/admin/AdminDashboard.vue';
import Agents from '../views/admin/Agents.vue';
import Configuration from '../views/admin/Configuration.vue';
import DigitalEntities from '../views/admin/DigitalEntities.vue';
import AdminLocations from '../views/admin/Locations.vue';
import MaterialEntities from '../views/admin/MaterialEntities.vue';
import Memos from '../views/admin/Memos.vue';
import Projects from '../views/admin/Projects.vue';
import Sections from '../views/admin/Sections.vue';
// 🚀 TEST : Import de la version optimisée
import SchemasNew from '../views/admin/SchemasNew.vue';
import Traductions from '../views/admin/Traductions.vue';
import Users from '../views/admin/Users.vue';

const routes = [
  { path: '/', name: 'Anamnesis', component: Home },
  
  // Pages publiques
  { 
    path: '/fonctionnalites', 
    name: 'Fonctionnalites', 
    component: Fonctionnalites,
    meta: { title: 'Fonctionnalités' }
  },
  { 
    path: '/actualites', 
    name: 'Actualites', 
    component: Actualites,
    meta: { title: 'Actualités' }
  },
  { 
    path: '/cas-usage', 
    name: 'CasUsage', 
    component: CasUsage,
    meta: { title: 'Cas d\'usage' }
  },
  { 
    path: '/contact', 
    name: 'Contact', 
    component: Contact,
    meta: { title: 'Contact' }
  },
  { 
    path: '/legal', 
    name: 'Legal', 
    component: Legal,
    meta: { title: 'Mentions Légales' }
  },
  
  // Routes d'authentification
  { 
    path: '/auth', 
    name: 'auth', 
    component: AuthPage,
    meta: { 
      requiresGuest: true, // Accessible uniquement si non connecté
      title: 'Authentification'
    }
  },
  { 
    path: '/login', 
    redirect: '/auth' 
  },
  { 
    path: '/register', 
    redirect: { name: 'auth', query: { mode: 'register' } }
  },
  { 
    path: '/forgot-password', 
    redirect: { name: 'auth', query: { mode: 'forgot-password' } }
  },
  { 
    path: '/validate-email', 
    name: 'email-validation', 
    component: EmailValidation,
    meta: { 
      requiresGuest: true,
      title: 'Validation Email'
    }
  },
  
  // Dashboard utilisateur
  { 
    path: '/dashboard', 
    name: 'UserDashboard', 
    component: UserDashboard,
    meta: { requiresAuth: true }
  },
  
  // Routes utilisateur
  { 
    path: '/form', 
    name: 'Formulaire', 
    component: Form,
    meta: { requiresAuth: true }
  },
  { 
    path: '/import-memos', 
    name: 'ImportMemos', 
    component: () => import('../views/user/ImportMemos.vue'),
    meta: { 
      requiresAuth: true,
      title: 'Importer MEMoS'
    }
  },
  { 
    path: '/carte-interactive', 
    name: 'CarteInteractive', 
    component: InteractiveMap,
    meta: { 
      requiresAuth: true,
      title: 'Carte Interactive'
    }
  },
  { 
    path: '/mymemos', 
    redirect: '/gestion-memos'
  },
  { 
    path: '/mylocations', 
    name: 'Mes lieux', 
    component: MyLocations,
    meta: { requiresAuth: true }
  },
  { 
    path: '/mymemos/:uuid', 
    name: 'MemosDetail', 
    component: MemosDetail,
    props: true,
    meta: { requiresAuth: true }
  },
  { 
    path: '/memos/:uuid', 
    name: 'MemosDetailPublic', 
    component: MemosDetail,
    props: true,
    meta: { requiresAuth: false }
  },
  { 
    path: '/myprofile', 
    name: 'MyProfile', 
    component: MyProfileEditor,
    meta: { requiresAuth: true }
  },
  { 
    path: '/mydigitalentities', 
    name: 'MyDigitalEntities', 
    component: MyDigitalEntities,
    meta: { requiresAuth: true }
  },
  { 
    path: '/mymaterialentities', 
    name: 'MyMaterialEntities', 
    component: MyMaterialEntities,
    meta: { requiresAuth: true }
  },
  { 
    path: '/myagents', 
    name: 'MyAgents', 
    component: MyAgents,
    meta: { requiresAuth: true }
  },
  { 
    path: '/myprojects', 
    name: 'MyProjects', 
    component: MyProjects,
    meta: { requiresAuth: true }
  },
  {
    path: '/collections',
    name: 'CollectionsManagement',
    component: CollectionsManagement,
    meta: { requiresAuth: true, title: 'Mes Collections' }
  },
  {
    path: '/collections/:id',
    name: 'CollectionView',
    component: CollectionView,
    props: true,
    meta: { requiresAuth: true, title: 'Détail de la Collection' }
  },
  {
    path: '/export-tools',
    name: 'AdvancedExportTools',
    component: () => import('../views/user/AdvancedExportTools.vue'),
    meta: { 
      requiresAuth: true,
      title: 'Outils d\'Export Avancé'
    }
  },
  {
    path: '/export-data',
    name: 'ExportData',
    component: () => import('../views/user/ExportData.vue'),
    meta: { 
      requiresAuth: true,
      title: 'Export de mes données'
    }
  },
  {
    path: '/gestion-memos',
    name: 'GestionMemos',
    component: GestionMemos,
    meta: { requiresAuth: true, title: 'Gestion MEMoS' }
  },
  
  // Dashboard administrateur
  { 
    path: '/admin', 
    name: 'AdminDashboard', 
    component: AdminDashboard,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  
  // Routes administratives
  { 
    path: '/admin_sections', 
    name: 'AdminSections', 
    component: Sections,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/admin_traductions', 
    name: 'AdminTraductions', 
    component: Traductions,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/admin_configuration', 
    name: 'AdminConfiguration', 
    component: Configuration,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/admin_schemas', 
    name: 'AdminSchemas', 
    component: SchemasNew, // 🚀 TEST : Version optimisée
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/admin_users', 
    name: 'AdminUsers', 
    component: Users,
    meta: { 
      requiresAuth: true,
      requiresSuperAdmin: true 
    }
  },
  { 
    path: '/admin_locations', 
    name: 'AdminLocations', 
    component: AdminLocations,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/admin_agents', 
    name: 'AdminAgents', 
    component: Agents,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/admin_material_entities', 
    name: 'AdminMaterialEntities', 
    component: MaterialEntities,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/admin_digital_entities', 
    name: 'AdminDigitalEntities', 
    component: DigitalEntities,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/admin_projects', 
    name: 'AdminProjects', 
    component: Projects,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/admin_memos', 
    name: 'AdminMemos', 
    component: Memos,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/admin/export-mappings', 
    name: 'AdminExportMappings', 
    component: () => import('../views/admin/ExportMappings.vue'),
    meta: { 
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Mappings d\'Export'
    }
  },
  { 
    path: '/admin/export-mappings/new', 
    name: 'AdminNewExportMapping', 
    component: () => import('../views/admin/mappings/ExportMappingEditor.vue'),
    meta: { 
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Nouveau Mapping d\'Export'
    }
  },
  { 
    path: '/admin/export-mappings/:uuid', 
    name: 'AdminEditExportMapping', 
    component: () => import('../views/admin/mappings/ExportMappingEditor.vue'),
    props: true,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  { 
    path: '/admin/metamodel-translations', 
    name: 'AdminMetamodelTranslations', 
    component: () => import('../views/admin/MetamodelTranslations.vue'),
    meta: { 
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Traductions du Métamodèle'
    }
  },
  { 
    path: '/schemas', 
    name: 'Schemas', 
    component: SchemasNew, // 🚀 TEST : Version optimisée
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/users', 
    name: 'Users', 
    component: Users,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  { 
    path: '/locations', 
    name: 'Locations', 
    component: AdminLocations,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    }
  },
  {
    path: '/location/:uuid',
    name: 'LocationDetail',
    component: LocationDetail,
    props: true,
    meta: { requiresAuth: false }
  },
  {
    path: '/agent/:uuid',
    name: 'AgentDetail',
    component: AgentDetail,
    props: true,
    meta: { requiresAuth: false }
  },
  {
    path: '/material-entity/:uuid',
    name: 'MaterialEntityDetail',
    component: MaterialEntityDetail,
    props: true,
    meta: { requiresAuth: false }
  },
  {
    path: '/project/:uuid',
    name: 'ProjectDetail',
    component: ProjectDetail,
    props: true,
    meta: { requiresAuth: false }
  },
  {
    path: '/objects/:uuid',
    name: 'DigitalEntityDetail',
    component: DigitalEntityDetail,
    props: true,
    meta: { requiresAuth: false }
  },
  {
    path: '/digitalentity/:uuid',
    name: 'DigitalEntityDetail',
    component: DigitalEntityDetail,
    props: true,
    meta: { requiresAuth: false }
  },
  
  // Route 404 - doit être en dernier
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { title: 'Page introuvable' }
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

// Variable pour suivre l'état d'initialisation
let isAuthInitialized = false;
let authInitPromise: Promise<void> | null = null;

// Fonction d'initialisation de l'authentification
const initializeAuthForRouter = async (): Promise<void> => {
  if (isAuthInitialized) return;
  
  if (authInitPromise) {
    await authInitPromise;
    return;
  }

  authInitPromise = (async () => {
    try {
      const { useAuthStore } = await import('@/stores/auth');
      const authStore = useAuthStore();
      
      // Initialiser depuis le localStorage si pas déjà fait
      if (!authStore.token) {
        await authStore.initializeFromStorage();
      }
      
      // Si on a un token, essayer de récupérer les infos utilisateur
      if (authStore.token && !authStore.user) {
        try {
          await authStore.fetchCurrentUser();
        } catch (error) {
          // Si échec, le token sera nettoyé automatiquement
          console.warn('Échec de récupération des données utilisateur:', error);
        }
      }
      
      isAuthInitialized = true;
    } catch (error) {
      console.error('Erreur lors de l\'initialisation auth pour le router:', error);
      isAuthInitialized = true; // Marquer comme initialisé même en cas d'erreur
    }
  })();

  await authInitPromise;
};

// Guards d'authentification
router.beforeEach(async (to, from, next) => {
  // S'assurer que l'authentification est initialisée
  await initializeAuthForRouter();
  
  // Import dynamique pour éviter les dépendances circulaires
  const { useAuthStore } = await import('@/stores/auth');
  const authStore = useAuthStore();

  // Vérifier si la route nécessite une authentification
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  const requiresSuperAdmin = to.matched.some(record => record.meta.requiresSuperAdmin);
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest);

  // Si l'utilisateur est connecté et essaie d'accéder aux pages d'auth
  if (requiresGuest && authStore.isAuthenticated) {
    next('/dashboard');
    return;
  }

  // Si la route nécessite une authentification
  if (requiresAuth) {
    if (!authStore.isAuthenticated) {
      // Rediriger vers la page d'authentification avec l'URL de retour
      next({
        name: 'auth',
        query: { redirect: to.fullPath }
      });
      return;
    }

    // Vérifier les permissions admin si nécessaire
    if (requiresAdmin && !authStore.isAdmin) {
      // Rediriger vers le dashboard utilisateur
      next('/dashboard');
      return;
    }

    // Vérifier les permissions superadmin si nécessaire
    if (requiresSuperAdmin && !authStore.isSuperAdmin) {
      // Rediriger vers la page d'accueil
      next('/');
      return;
    }

    // Vérifier la validité du token
    if (!(await authStore.checkTokenExpiry())) {
      next({
        name: 'auth',
        query: { redirect: to.fullPath }
      });
      return;
    }
  }

  // Continuer la navigation
  next();
});

export default router;
